// Package imports:
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faCheck } from '@fortawesome/pro-regular-svg-icons'
// Style imports:
// import './DataStatusToast.scss';
// Component imports:
import SpinnerIcon from '../common/SpinnerIcon';
import Toast from 'react-bootstrap/Toast';
import Table from 'react-bootstrap/Table';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { PrivateDataRequested, Requested, BY_LEVEL_SYMBOLS } from '../../types/api/Types';
import { isPrivateDataError, isPrivateDataErrorSimple } from '../../types/api/PrivateTypes';
import { isUserLoggedIn } from '../../services/loginUtils';

type DataRequests = 'Public Orders' | 'Public Trades' | 'Alerts' | 'News' | 'Bookmarks' | 'Private Data';
interface DataRequestObject {
    title: DataRequests,
    data: Requested<any> | PrivateDataRequested<any>
}

type DataRequestFinishedMap = {
    [key in DataRequests]: boolean
}

interface IOwnProps {
    symbol: string;
};
type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps & IOwnProps;

const TOAST_DISAPPEARS_IN_N_MS = 2000;

const DataStatusToast: React.FC<Props> = ({
    symbol,
    currentTradingDay,
    privateOrderAndTradeDoubleMap,
    publicOrderDoubleMap,
    publicOrderByLevelDoubleMap,
    publicTradeDoubleMap,
    alertsByDate,
    newsDoubleMap,
    bookmarks,
    bookmarkError,
    hasEmsToken
}) => {
    const getInitialDataRequestStatePrivate = (hasEmsToken: boolean) => ({
        'Public Orders': false,
        'Public Trades': false,
        'Alerts': false,
        'News': false,
        'Bookmarks': false,
        'Private Data': hasEmsToken ? false : true
    });

    const getInitialDataRequestState = () => ({
        'Public Orders': false,
        'Public Trades': false,
        'Alerts': false,
        'News': false,
        'Bookmarks': false,
        'Private Data': false
    });

    const [status, setStatus] = useState<'initial' | 'error-minimized' | 'error-maximized' | 'hidden'>('initial');
    const [ dataRequestFinishedMap, setDataRequestFinishedMap ] = useState<DataRequestFinishedMap>(getInitialDataRequestStatePrivate(hasEmsToken));
    const [ hasError, setHasError ] = useState<DataRequestFinishedMap>(getInitialDataRequestState());
    const [ dataRequestObjectsRender, setDataRequestObjectsRender ] = useState<DataRequestObject[]>([]);
    const hasAnyError = Object.values(hasError).some(Boolean);

    useEffect(() => {
        setStatus('initial');
        setDataRequestFinishedMap(getInitialDataRequestStatePrivate(hasEmsToken));
        setHasError(getInitialDataRequestState());
    }, [symbol, currentTradingDay, hasEmsToken]);

    useEffect(() => {
        const finishedBoolArray = Object.values(dataRequestFinishedMap);
        const hasErrorArray = Object.values(hasError);
        const anyError = hasErrorArray.some(Boolean);

        if (finishedBoolArray.every((finished) => finished)) {
            const windowTimeoutId = window.setTimeout(() => {
                if (anyError) {
                    setStatus('error-minimized');
                } else {
                    setStatus('hidden'); // Hide toast when all succeed
                }
            }, TOAST_DISAPPEARS_IN_N_MS);
            return () => {
                window.clearTimeout(windowTimeoutId);
            };
        }
    }, [dataRequestFinishedMap, hasError]);

    useEffect(() => {
        const dataRequestObjects: DataRequestObject[] = [{
            title: 'Public Orders',
            data: (BY_LEVEL_SYMBOLS.includes(symbol))
                ? publicOrderByLevelDoubleMap?.[symbol]?.[currentTradingDay]
                : publicOrderDoubleMap?.[symbol]?.[currentTradingDay]
        }, {
            title: 'Public Trades',
            data: publicTradeDoubleMap?.[symbol]?.[currentTradingDay]
        }, {
            title: 'Alerts',
            data: alertsByDate?.[currentTradingDay]
        }, {
            title: 'News',
            data: newsDoubleMap?.[symbol]?.[currentTradingDay]
        }, {
            title: 'Bookmarks',
            data: (bookmarks === null || bookmarks === 'waiting') // if
                ? 'waiting'
                : (bookmarkError !== null) // else if
                ? new Error(bookmarkError)
                : bookmarks
        }];
    
        const privateData = privateOrderAndTradeDoubleMap?.[symbol]?.[currentTradingDay];
    
        if (hasEmsToken && privateData !== undefined) {
            const data = isPrivateDataErrorSimple(privateData) ? new Error(privateData.error) : privateData;

            dataRequestObjects.push({
                title: 'Private Data',
                data: data
            });
        }
        setDataRequestObjectsRender(dataRequestObjects);
    }, [symbol, currentTradingDay, privateOrderAndTradeDoubleMap, publicOrderDoubleMap, publicOrderByLevelDoubleMap, publicTradeDoubleMap, alertsByDate, newsDoubleMap, bookmarks, bookmarkError, hasEmsToken]);

    const getAppropriateIconFromRequested = <T extends any>(requestedData: Requested<T> | PrivateDataRequested<T>, dataTitle: DataRequests) => {
        if (hasError[dataTitle]) {
            return (
                <FontAwesomeIcon icon={faExclamationTriangle} className='error-red' />
            );
        }
        if (requestedData === undefined || requestedData === 'waiting') {
            return <SpinnerIcon />;
        } else if (requestedData instanceof Error) {
            if (!dataRequestFinishedMap[dataTitle]) {
                setDataRequestFinishedMap({
                    ...dataRequestFinishedMap,
                    [dataTitle]: true
                });
                setHasError({
                    ...hasError,
                    [dataTitle]: true
                });
            }
            return (
                <FontAwesomeIcon icon={faExclamationTriangle} className='error-red' title={requestedData.message} />
            );
        } else if (hasEmsToken && isPrivateDataError(requestedData)) {
            if (!dataRequestFinishedMap[dataTitle]) {
                setDataRequestFinishedMap({
                    ...dataRequestFinishedMap,
                    [dataTitle]: true
                });
                setHasError({
                    ...hasError,
                    [dataTitle]: true
                });
            }
            return (
                <FontAwesomeIcon icon={faExclamationTriangle} className='error-red' title={requestedData.error} />
            );
        } else {
            if (!dataRequestFinishedMap[dataTitle] || hasError[dataTitle]) {
                setDataRequestFinishedMap({
                    ...dataRequestFinishedMap,
                    [dataTitle]: true
                });
                setHasError({
                    ...hasError,
                    [dataTitle]: false
                });

                setTimeout(() => {
                    removeDataRequestObject(dataTitle);
                }, 2000); // 10 seconds delay
            }
            return (
                <FontAwesomeIcon icon={faCheck} className='green' />
            );
        }
    }

    // ============================= HELPER FUNCTIONS =============================
    // =========----- Remove Data Request Object -----=========
    const removeDataRequestObject = (titleToRemove: string) => {
        setDataRequestObjectsRender(prevDataRequestObjectsRender => 
            prevDataRequestObjectsRender.filter(obj => obj.title !== titleToRemove)
        );
    };

    return (
        <>
            {status === 'initial' && (
                <Toast id='DataStatusToast' show={true} animation={false}>
                    <Toast.Body>
                        <Table className='SimpleCompressedTable'>
                            <tbody>
                                {
                                    dataRequestObjectsRender.map(({ title, data }) => (
                                        <tr key={title}>
                                            <th scope='col'>{title}</th>
                                            <td>{getAppropriateIconFromRequested(data, title)}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </Table>
                    </Toast.Body>
                </Toast>
            )}
            {status === 'error-maximized' && (
                <Toast id='DataStatusToast' show={true} animation={false}>
                    <Toast.Body>
                        <Table className='SimpleCompressedTable'>
                            <tbody>
                                {
                                    dataRequestObjectsRender.map(({ title, data }) => (
                                        <tr key={title}>
                                            <th scope='col'>{title}</th>
                                            <td>{getAppropriateIconFromRequested(data, title)}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </Table>
                        <div className='minimize-toast-btn-container'>
                            <button className='minimize-toast-btn' onClick={() => setStatus('error-minimized')} title='Minimize'>
                                &#10005;
                            </button>
                        </div>
                    </Toast.Body>
                </Toast>
            )}
            {status === 'error-minimized' && hasAnyError && (
                <div className='maximize-toast-btn-container'>
                    <button className='maximize-toast-btn' onClick={() => setStatus('error-maximized')} title='Show error details'>
                        <FontAwesomeIcon icon={faExclamationTriangle} className='error-red' />
                    </button>
                </div>
            )}
            {/* Hide toast when status is 'hidden' */}
        </>
    );
}

function mapStateToProps({ dashboardReducer, loginReducer, privateReducer, publicReducer, alertReducer, newsReducer, bookmarkReducer }: RootState) {
    const { privateOrderAndTradeDoubleMap } = privateReducer;
    const { publicOrderDoubleMap, publicTradeDoubleMap, publicOrderByLevelDoubleMap } = publicReducer;
    const { newsDoubleMap } = newsReducer;
    const { currentTradingDay } = dashboardReducer;
    const { hasEmsToken } = isUserLoggedIn(loginReducer);
    const { alertsByDate } = alertReducer
    const { bookmarks, bookmarkError } = bookmarkReducer;
    return {
        currentTradingDay,
        privateOrderAndTradeDoubleMap,
        publicOrderByLevelDoubleMap,
        publicOrderDoubleMap,
        publicTradeDoubleMap,
        alertsByDate,
        newsDoubleMap,
        bookmarks,
        bookmarkError,
        hasEmsToken
    };
}

const connector = connect(mapStateToProps);
export default connector(DataStatusToast);